import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PurposeDrivenLogo, OurSquareLogo, BRAND_COLORS, OUR_SQUARE_COLORS } from '../components/brand';
import { 
  Users, Shield, UserCog, Building, BarChart3, Heart,
  ArrowRight, Sparkles, Home, Wrench, User, Flame
} from 'lucide-react';

const DEMO_ACCOUNTS = [
  {
    role: 'client',
    label: 'Client / Participant',
    email: 'client@purposedriven.org',
    password: 'client123',
    name: 'James Wilson',
    desc: 'Recovery journey: check-ins, milestones, employment hub, courses, achievements',
    icon: Heart,
    color: 'from-teal-500 to-emerald-500',
    bg: 'bg-teal-50 border-teal-200',
  },
  {
    role: 'peer_specialist',
    label: 'Peer Support Specialist',
    email: 'peer@purposedriven.org',
    password: 'peer123',
    name: 'Lisa Chen',
    desc: 'Client management, session notes, peer documentation, referrals',
    icon: Users,
    color: 'from-sky-500 to-blue-500',
    bg: 'bg-sky-50 border-sky-200',
  },
  {
    role: 'facilitator',
    label: 'Facilitator',
    email: 'facilitator@heat.com',
    password: 'facilitator123',
    name: 'Dr. Angela Foster',
    desc: 'HEAT/HER curriculum delivery, attendance tracking, module facilitation',
    icon: Flame,
    color: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-50 border-orange-200',
  },
  {
    role: 'coordinator',
    label: 'Program Coordinator',
    email: 'coordinator@purposedriven.org',
    password: 'coord123',
    name: 'Marcus Johnson',
    desc: 'Program oversight, analytics, specialists, flags, group sessions',
    icon: UserCog,
    color: 'from-violet-500 to-purple-500',
    bg: 'bg-violet-50 border-violet-200',
  },
  {
    role: 'court_officer',
    label: 'Referral Source',
    email: 'court@purposedriven.org',
    password: 'court123',
    name: 'Judge Patricia Brown',
    desc: 'Compliance monitoring, services overview, participant records (view-only)',
    icon: Shield,
    color: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50 border-amber-200',
  },
  {
    role: 'admin',
    label: 'Executive Director / Admin',
    email: 'admin@purposedriven.org',
    password: 'admin123',
    name: 'Sarah Mitchell',
    desc: 'Full platform oversight, outcome metrics, user management, all messages',
    icon: BarChart3,
    color: 'from-rose-500 to-pink-500',
    bg: 'bg-rose-50 border-rose-200',
  },
  {
    role: 'resource_partner',
    label: 'Community Resource Partner',
    email: 'partner@test.org',
    password: 'partner123',
    name: 'Michael Rodriguez',
    desc: 'Organization profile, post opportunities, referrals, community insights',
    icon: Building,
    color: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-50 border-emerald-200',
  },
];

const OUR_SQUARE_ACCOUNTS = [
  {
    role: 'housing_coordinator',
    label: 'Housing Coordinator',
    email: 'coordinator@oursquare.org',
    password: 'coord123',
    name: 'Marcus Johnson',
    desc: 'Full property management, residents, reports, staff oversight, rent tracking',
    icon: UserCog,
    color: 'from-cyan-600 to-teal-600',
  },
  {
    role: 'housing_staff',
    label: 'Housing Staff',
    email: 'staff@oursquare.org',
    password: 'staff123',
    name: 'Terri Williams',
    desc: 'Day-to-day operations, curfew checks, incidents, meetings, case notes',
    icon: Wrench,
    color: 'from-teal-600 to-emerald-600',
  },
  {
    role: 'housing_resident',
    label: 'Resident',
    email: 'resident@oursquare.org',
    password: 'resident123',
    name: 'Sarah Mitchell',
    desc: 'View progress, submit tasks, track milestones, message peer specialist, housing info',
    icon: User,
    color: 'from-purple-600 to-pink-600',
    redirectTo: '/my-progress',
  },
];

const HER_ACCOUNTS = [
  {
    role: 'client',
    label: 'HER Participant',
    email: 'client@purposedriven.org',
    password: 'client123',
    name: 'Angela Brooks',
    desc: 'Complete modules, submit reflections, track progress with peer support',
    icon: Heart,
    color: 'from-rose-500 to-pink-500',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her',
  },
  {
    role: 'peer_specialist',
    label: 'HER Peer Specialist',
    email: 'peer@purposedriven.org',
    password: 'peer123',
    name: 'Lisa Chen',
    desc: 'Support participants, review module requests, document engagement',
    icon: Users,
    color: 'from-rose-600 to-fuchsia-500',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her',
  },
  {
    role: 'facilitator',
    label: 'HER Facilitator',
    email: 'facilitator@purposedriven.org',
    password: 'fac123',
    name: 'Jordan Lee',
    desc: 'Deliver HER curriculum, track attendance, document module progress',
    icon: User,
    color: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her',
  },
  {
    role: 'coordinator',
    label: 'HER Program Coordinator',
    email: 'coordinator@purposedriven.org',
    password: 'coord123',
    name: 'Marcus Johnson',
    desc: 'Coordinate day-to-day programming, review approvals, manage facilitators',
    icon: UserCog,
    color: 'from-pink-500 to-rose-500',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her',
  },
  {
    role: 'court_officer',
    label: 'HER Referral Source',
    email: 'court@purposedriven.org',
    password: 'court123',
    name: 'Judge Patricia Brown',
    desc: 'View referred client progress, attendance, and documentation',
    icon: Shield,
    color: 'from-rose-500 to-red-500',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her/referral-portal',
  },
  {
    role: 'admin',
    label: 'HER Executive Director',
    email: 'admin@purposedriven.org',
    password: 'admin123',
    name: 'Sarah Mitchell',
    desc: 'High-level oversight, final approvals, monitor program outcomes',
    icon: BarChart3,
    color: 'from-rose-600 to-pink-600',
    bg: 'bg-rose-50 border-rose-200',
    redirectTo: '/her',
  },
];

const HEAT_ACCOUNTS = [
  {
    role: 'client',
    label: 'HEAT Participant',
    email: 'client@purposedriven.org',
    password: 'client123',
    name: 'James Wilson',
    desc: 'Complete modules, submit reflections, track progress with peer support',
    icon: Heart,
    color: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-50 border-orange-200',
    redirectTo: '/heat',
  },
  {
    role: 'peer_specialist',
    label: 'HEAT Peer Specialist',
    email: 'peer@purposedriven.org',
    password: 'peer123',
    name: 'Lisa Chen',
    desc: 'Support participants, review module requests, document engagement',
    icon: Users,
    color: 'from-orange-600 to-amber-600',
    bg: 'bg-orange-50 border-orange-200',
    redirectTo: '/heat',
  },
  {
    role: 'facilitator',
    label: 'HEAT Facilitator',
    email: 'facilitator@purposedriven.org',
    password: 'fac123',
    name: 'Jordan Lee',
    desc: 'Deliver HEAT curriculum, track attendance, document module progress',
    icon: User,
    color: 'from-orange-500 to-amber-600',
    bg: 'bg-orange-50 border-orange-200',
    redirectTo: '/heat',
  },
  {
    role: 'coordinator',
    label: 'HEAT Program Coordinator',
    email: 'coordinator@purposedriven.org',
    password: 'coord123',
    name: 'Marcus Johnson',
    desc: 'Coordinate day-to-day programming, review approvals, manage facilitators',
    icon: UserCog,
    color: 'from-amber-500 to-yellow-500',
    bg: 'bg-amber-50 border-amber-200',
    redirectTo: '/heat',
  },
  {
    role: 'court_officer',
    label: 'HEAT Referral Source',
    email: 'court@purposedriven.org',
    password: 'court123',
    name: 'Judge Patricia Brown',
    desc: 'View referred client progress, attendance, and documentation',
    icon: Shield,
    color: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50 border-orange-200',
    redirectTo: '/heat/referral-portal',
  },
  {
    role: 'admin',
    label: 'HEAT Executive Director',
    email: 'admin@purposedriven.org',
    password: 'admin123',
    name: 'Sarah Mitchell',
    desc: 'High-level oversight, final approvals, monitor program outcomes',
    icon: BarChart3,
    color: 'from-amber-600 to-orange-600',
    bg: 'bg-amber-50 border-amber-200',
    redirectTo: '/heat',
  },
];


export { DEMO_ACCOUNTS };

export default function DemoPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [showPurposeDriven, setShowPurposeDriven] = useState(false);
  const [showOurSquare, setShowOurSquare] = useState(false);
  const [showHer, setShowHer] = useState(false);
  const [showHeat, setShowHeat] = useState(false);

  const handleDemoLogin = async (account, redirectTo = '/dashboard') => {
    setLoading(account.role);
    try {
      // Clear any existing session first to prevent stale cookie conflicts
      sessionStorage.removeItem('pd_user');
      await login(account.email, account.password);
      localStorage.setItem('demo_mode', 'true');
      navigate(redirectTo);
    } catch (e) {
      setLoading(null);
      // Show user-friendly error if login fails
      const detail = e?.response?.data?.detail || 'Login failed. This demo account may not be configured.';
      alert(detail);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom right, #f8fafc, white, ${BRAND_COLORS.lightBg}30)` }} data-testid="demo-page">
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div 
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
            style={{ backgroundColor: `${BRAND_COLORS.primary}15`, border: `1px solid ${BRAND_COLORS.primary}30`, color: BRAND_COLORS.primary }}
          >
            <Sparkles size={14} /> Demo Mode
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Select Your Role</h1>
          <p className="text-muted-foreground text-base max-w-xl mx-auto" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            Explore both platforms from any role. Click a role below to access its dashboard and features.
          </p>
        </div>

        {/* Peer Support Services Section */}
        <div className="mb-10">
          <div className="text-center mb-6">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
              style={{ backgroundColor: `${BRAND_COLORS.primary}15`, border: `1px solid ${BRAND_COLORS.primary}30`, color: BRAND_COLORS.primary }}
              data-testid="purpose-driven-pill"
            >
              <PurposeDrivenLogo variant="icon" size="xs" className="h-4 w-4" /> Peer Support Services
            </div>
          </div>

          {!showPurposeDriven ? (
            <Card
              className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 max-w-lg mx-auto"
              onClick={() => setShowPurposeDriven(true)}
              data-testid="purpose-driven-card"
            >
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <PurposeDrivenLogo size="md" className="h-20 w-auto" />
                </div>
                <h3 className="font-bold text-lg text-slate-900">Peer Support Services</h3>
                <p className="text-sm text-slate-600 mt-2">Counseling &amp; Peer Support</p>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Access peer support, recovery journeys, documentation, referrals, and analytics for clients.
                </p>
                <div className="flex items-center justify-center gap-1 mt-4 text-teal-700 text-xs font-medium">
                  Select a role to enter <ArrowRight size={14} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="purpose-driven-roles">
              {DEMO_ACCOUNTS.map((account) => {
                const Icon = account.icon;
                const isLoading = loading === account.role;
                return (
                  <Card
                    key={account.role}
                    className={`border rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${account.bg} ${isLoading ? 'opacity-70' : ''}`}
                    onClick={() => !loading && handleDemoLogin(account)}
                    data-testid={`demo-role-${account.role}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${account.color} flex items-center justify-center`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        {isLoading ? (
                          <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ArrowRight size={18} className="text-muted-foreground mt-1" />
                        )}
                      </div>
                      <h3 className="font-semibold text-sm text-slate-900">{account.label}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">as {account.name}</p>
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{account.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Our Square Housing Section */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-semibold">
              <img src="/images/our-square-logo.png" alt="" className="h-4 w-auto" /> Transitional Housing
            </div>
          </div>

          {!showOurSquare ? (
            <Card
              className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 max-w-lg mx-auto"
              onClick={() => setShowOurSquare(true)}
              data-testid="our-square-card"
            >
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <img src="/images/our-square-logo.png" alt="Our Square" className="h-20 w-auto object-contain" />
                </div>
                <h3 className="font-bold text-lg text-slate-900">Transitional Housing</h3>
                <p className="text-sm text-slate-600 mt-2">Transitional Living &amp; Sober Living for Women</p>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Full housing program management for women: properties, residents, childcare assistance, accountability, compliance reporting, rent tracking, and peer support integration.
                </p>
                <div className="flex items-center justify-center gap-1 mt-4 text-cyan-700 text-xs font-medium">
                  Select a role to enter <ArrowRight size={14} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="max-w-2xl mx-auto space-y-3" data-testid="our-square-roles">
              <p className="text-center text-xs text-muted-foreground mb-2">Select your Our Square role:</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {OUR_SQUARE_ACCOUNTS.map((account) => {
                  const Icon = account.icon;
                  const isLoading = loading === account.role;
                  return (
                    <Card
                      key={account.role}
                      className={`border-2 border-cyan-200 bg-cyan-50/50 rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${isLoading ? 'opacity-70' : ''}`}
                      onClick={() => !loading && handleDemoLogin(account, account.redirectTo || '/our-square')}
                      data-testid={`demo-role-${account.role}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${account.color} flex items-center justify-center`}>
                            <Icon size={20} className="text-white" />
                          </div>
                          {isLoading ? (
                            <div className="w-5 h-5 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ArrowRight size={18} className="text-muted-foreground mt-1" />
                          )}
                        </div>
                        <h3 className="font-semibold text-sm text-slate-900">{account.label}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">as {account.name}</p>
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{account.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <button onClick={() => setShowOurSquare(false)} className="text-xs text-muted-foreground mx-auto block mt-2 hover:text-slate-600">
                &larr; Back to all roles
              </button>
            </div>
          )}
        </div>

        {/* HER Program Section */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold" data-testid="her-pill">
              <img src="/images/heat-logo.png" alt="" className="h-4 w-auto" /> H.E.A.T. for Her
            </div>
          </div>

          {!showHer ? (
            <Card
              className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 max-w-lg mx-auto"
              onClick={() => setShowHer(true)}
              data-testid="her-card"
            >
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <img src="/images/heat-logo.png" alt="H.E.A.T. for Her" className="h-16 w-auto object-contain" />
                </div>
                <h3 className="font-bold text-lg text-slate-900">H.E.A.T. for Her</h3>
                <p className="text-sm text-slate-600 mt-2">Habitual Empowerment Program</p>
                <p className="text-xs text-muted-foreground mt-1">Participant success</p>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Track HER sessions, attendance sign-ins, facilitator bios, and documentation snapshots.
                </p>
                <div className="flex items-center justify-center gap-1 mt-4 text-rose-700 text-xs font-medium">
                  Select a role to enter <ArrowRight size={14} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="max-w-5xl mx-auto space-y-3" data-testid="her-roles">
              <p className="text-center text-xs text-muted-foreground mb-2">Select your HER role:</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {HER_ACCOUNTS.map((account) => {
                  const Icon = account.icon;
                  const isLoading = loading === account.role;
                  return (
                    <Card
                      key={account.role}
                      className={`border rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${account.bg} ${isLoading ? 'opacity-70' : ''}`}
                      onClick={() => !loading && handleDemoLogin(account, account.redirectTo || '/her')}
                      data-testid={`her-role-${account.role}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${account.color} flex items-center justify-center`}>
                            <Icon size={20} className="text-white" />
                          </div>
                          {isLoading ? (
                            <div className="w-5 h-5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ArrowRight size={18} className="text-muted-foreground mt-1" />
                          )}
                        </div>
                        <h3 className="font-semibold text-sm text-slate-900">{account.label}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">as {account.name}</p>
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{account.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <button onClick={() => setShowHer(false)} className="text-xs text-muted-foreground mx-auto block mt-2 hover:text-slate-600">
                &larr; Back to all roles
              </button>
            </div>
          )}
        </div>

        {/* HEAT Program Section */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200 text-orange-800 text-xs font-semibold" data-testid="heat-pill">
              <img src="/images/heat-logo.png" alt="" className="h-4 w-auto" /> H.E.A.T. for Men
            </div>
          </div>

          {!showHeat ? (
            <Card
              className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 max-w-lg mx-auto"
              onClick={() => setShowHeat(true)}
              data-testid="heat-card"
            >
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <img src="/images/heat-logo.png" alt="H.E.A.T." className="h-16 w-auto object-contain" />
                </div>
                <h3 className="font-bold text-lg text-slate-900">H.E.A.T. for Men</h3>
                <p className="text-sm text-slate-600 mt-2">Habitual Empowerment Accountability Therapy</p>
                <p className="text-xs text-muted-foreground mt-1">Participant success</p>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                  Track HEAT modules, attendance sign-ins, facilitator bios, and documentation snapshots.
                </p>
                <div className="flex items-center justify-center gap-1 mt-4 text-orange-700 text-xs font-medium">
                  Select a role to enter <ArrowRight size={14} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="max-w-5xl mx-auto space-y-3" data-testid="heat-roles">
              <p className="text-center text-xs text-muted-foreground mb-2">Select your HEAT role:</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {HEAT_ACCOUNTS.map((account) => {
                  const Icon = account.icon;
                  const isLoading = loading === account.role;
                  return (
                    <Card
                      key={account.role}
                      className={`border rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${account.bg} ${isLoading ? 'opacity-70' : ''}`}
                      onClick={() => !loading && handleDemoLogin(account, account.redirectTo || '/heat')}
                      data-testid={`heat-role-${account.role}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${account.color} flex items-center justify-center`}>
                            <Icon size={20} className="text-white" />
                          </div>
                          {isLoading ? (
                            <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ArrowRight size={18} className="text-muted-foreground mt-1" />
                          )}
                        </div>
                        <h3 className="font-semibold text-sm text-slate-900">{account.label}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">as {account.name}</p>
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{account.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <button onClick={() => setShowHeat(false)} className="text-xs text-muted-foreground mx-auto block mt-2 hover:text-slate-600">
                &larr; Back to all roles
              </button>
            </div>
          )}
        </div>

        {/* HEAT for Juvenile Section */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold" data-testid="heat-juvenile-pill">
              <Flame size={14} /> HEAT for Juvenile
            </div>
          </div>
          <Card className="border-2 border-dashed border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl max-w-lg mx-auto" data-testid="heat-juvenile-card">
            <CardContent className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center">
                  <Flame size={28} className="text-white" />
                </div>
              </div>
              <h3 className="font-bold text-lg text-slate-900">HEAT for Juvenile</h3>
              <p className="text-sm text-slate-600 mt-2">Juvenile curriculum and milestones coming soon.</p>
              <Button className="mt-4 rounded-full bg-amber-200 text-amber-900" disabled data-testid="heat-juvenile-coming-soon">
                Coming Soon
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          All data is sample data for demonstration purposes.
        </p>
      </div>
    </div>
  );
}
